import React, { useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import AppLayout from './components/layout/AppLayout';
import HomeHero from './components/home/HomeHero';
import StartPage from './components/start/StartPage';
import ReadingForm from './components/readings/ReadingForm';
import InterpretationView from './components/readings/InterpretationView';
import UploadReadingPhoto from './components/readings/UploadReadingPhoto';
import ReadingHistoryList from './components/readings/ReadingHistoryList';
import ReadingInsights from './components/readings/ReadingInsights';
import GuideHome from './components/guide/GuideHome';
import SpreadsGuide from './components/guide/SpreadsGuide';
import CardExplorer from './components/guide/CardExplorer';
import Tarot101 from './components/guide/Tarot101';
import AboutPage from './components/about/AboutPage';
import FaqPage from './components/faq/FaqPage';
import GuidedReading from './components/guided/GuidedReading';
import TarotCenter from './components/center/TarotCenter';
import DeckViewer from './components/deck/DeckViewer';
import VirtualReading from './components/virtual/VirtualReading';
import VirtualDeckPage from './components/deck/VirtualDeckPage';
import NextSteps from './components/readings/NextSteps';
import LoginPage from './components/auth/LoginPage';
import SignUpPage from './components/auth/SignUpPage';
import AdminPanel from './components/admin/AdminPanel';
import ProfilePage from './components/profile/ProfilePage';
import TestReading from './components/readings/TestReading';
import { useAuthStore } from './store/authStore';

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Admin route guard component
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, initialized } = useAuthStore();
  
  if (!initialized) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sun"></div>
      </div>
    );
  }
  
  if (!user || user.email !== 'taramccann@gmail.com') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// Protected route guard component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, initialized } = useAuthStore();
  
  if (!initialized) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sun"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

function AnimatedRoutes() {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<HomeHero />} />
        <Route path="/start" element={<StartPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignUpPage />} />
        <Route path="/guided" element={<GuidedReading />} />
        <Route path="/center" element={<TarotCenter />} />
        <Route path="/new-reading" element={<ReadingForm />} />
        <Route path="/test-reading" element={<TestReading />} />
        <Route path="/virtual" element={<VirtualReading />} />
        <Route path="/interpretation" element={<InterpretationView />} />
        <Route path="/upload-photo/:readingId" element={<UploadReadingPhoto />} />
        <Route path="/history" element={<ReadingHistoryList />} />
        <Route
          path="/insights"
          element={
            <ProtectedRoute>
              <ReadingInsights />
            </ProtectedRoute>
          }
        />
        <Route path="/guide" element={<GuideHome />} />
        <Route path="/guide/spreads" element={<SpreadsGuide />} />
        <Route path="/guide/cards" element={<CardExplorer />} />
        <Route path="/guide/101" element={<Tarot101 />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/faq" element={<FaqPage />} />
        <Route path="/deck" element={<DeckViewer />} />
        <Route path="/virtual-deck" element={<VirtualDeckPage />} />
        <Route path="/next-steps" element={<NextSteps />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminPanel />
            </AdminRoute>
          }
        />
      </Routes>
    </AnimatePresence>
  );
}

function App() {
  const { initializeAuth, initialized } = useAuthStore();
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (!hasInitializedRef.current) {
      initializeAuth();
      hasInitializedRef.current = true;
    }
  }, [initializeAuth]);

  if (!initialized) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sun"></div>
      </div>
    );
  }

  return (
    <Router>
      <ScrollToTop />
      <AppLayout>
        <AnimatedRoutes />
      </AppLayout>
    </Router>
  );
}

export default App;